import { getConfig } from '@superbet-group/web.lib.config';
import { isSuperbetByName } from '@superbet-group/web.lib.vue-utilities';

import { MarketAttribute } from '@core/constants';
import { OfferType } from '@models/offer/enums';

import BetSlipOdd from '../base/BetSlipOdd';
import PrimaryMarketOdd from '../base/PrimaryMarketOdd';
import {
    doneOddStatuses,
    OddStatus,
} from '../shared/odds/OddStatus';
import { isPriceBoostOdd } from '@sportOfferFederation/price-boost/utils';

const SUPERBET_HIGHLIGHT_TAG = 'highlight';

export interface ExtraFields {
    superbets?: string;
    tags?: string[];
}
export default class Odd implements BetSlipOdd, PrimaryMarketOdd {
    id: number;
    uuid: string;
    value: number;
    displayValue: string;
    description: string;
    marketId: number;
    marketUuid: string;
    marketOriginalName: string;
    marketName: string;
    marketTag?: string;
    status: string;
    marketOrder: number;
    // 1: preEvent , 2: live
    offerType: OfferType;
    tags?: string;
    code: string;
    name: string;
    extra?: { [key: string]: string };
    public isInPromotion: boolean;
    public isValidForSuperBonus: boolean;

    public superBetTags: string[] = [];
    initialName?: string;
    initialMarketName?: string;

    /**
     * Every match can have handicap market where you can place bets on regulars odds (1, X, 2) but with
     * handicap (handicap means advantage for some team and the whole purpose is to make betting more interesting)
     * Handicap is given to underdog team.  For example, if Barca plays with Dinamo, bookmakers give handicap to Dinamo
     * 1.5, and id you place bet on Barcelona to win, Barcelona must score +2 goals to beat 1.5 handicap.  Handicap
     * is advantage.  Handicap comes with + or -.
     *
     * odd has uniqueId as eventId oddId specialBetValue because we can have the same handicap market but with different
     * specialBetValues
     *
     * handicap(-1.5) 1 X 2
     * handicap(-2.5) 1 X 2
     * handicap(-3.5) 1 X 2
     */
    specialBetValue: string;
    showSpecialBetStatus: boolean;
    specifiers?: Record<string, string>;

    // the id isn't unique per market. To have a truly unique value the id has to be combined with the special bet value
    get uniqueId(): string {
        return `${this.id} ${this.specialBetValue}`;
    }

    public getUniqueMarketId(): string {
        return `${this.getUniqueMarketName()} ${this.marketId}`;
    }

    public getMarketName(): string {
        return this.marketName;
    }

    public getUniqueMarketName(): string {
        // TODO: Refactor markets disply logic. Check when specialBetValue needs to be displayed
        // we had if (showSpecialBetValueInMarketHeader) - always returned false

        // Offer returns concatenated odd names as `marketName` for SGA (superbets) Price Boosts
        //  which breaks market grouping. Use tag instead.
        if (isPriceBoostOdd(this)) {
            return 'price_boost';
        } else {
            return this.marketName;
        }
    }

    public isMain(): boolean {
        return this.hasMarketTag(MarketAttribute.main);
    }

    public hasMarketTag(tag: string): boolean {
        return !!this.marketTag && this.marketTag.includes(tag);
    }

    public isMarketType(marketName: string): boolean {
        return this.tags && this.tags.includes(marketName) || false;
    }

    // todo test
    public isEqualById(compareOdd: Odd): boolean {
        return this.id === compareOdd.id && this.specialBetValue === compareOdd.specialBetValue;
    }

    public static copy(odd: Odd): Odd | null {
        if (!odd) {
            return null;
        }
        const newInstance = new Odd();
        Object.assign(newInstance, odd);
        return newInstance;
    }

    get isFinished(): boolean {
        return doneOddStatuses[this.status as OddStatus];
    }

    get isShownOnOffer(): boolean {
        return this.status === OddStatus.active || this.status === OddStatus.block;
    }

    get isLocked(): boolean {
        return this.status !== OddStatus.active;
    }

    get isBonusable(): boolean {
        if (getConfig().superBonus.isEnabled) {
            return this.isValidForSuperBonus;
        } else {
            return !this.isInPromotion;
        }
    }

    setSuperBetFields(extraFields: ExtraFields): void {
        this.superBetTags = extraFields.tags || [];
    }

    get isHighlightedSuperBet(): boolean {
        return this.superBetTags.includes(SUPERBET_HIGHLIGHT_TAG);
    }

    get isSuperbet() {
        return this.name != null && isSuperbetByName(this.name) && !isPriceBoostOdd(this);
    }
}

// this.value-bind uses for...in which only iterates over own properties on the object
// getters on a class aren't own properties. This marks them as enumerable and
// makes the usable in v-bind
Object.defineProperty(Odd.prototype, 'isFinished', { enumerable: true });
Object.defineProperty(Odd.prototype, 'isShownOnOffer', { enumerable: true });
Object.defineProperty(Odd.prototype, 'isLocked', { enumerable: true });
Object.defineProperty(Odd.prototype, 'isBonusable', { enumerable: true });
